import React, { useState, useRef } from 'react'
import { Button } from '../../ui/Button'
import { Input } from '../../ui/Input'
import { FileUpload } from '../../ui/FileUpload'
import { Formik, Form, FormikProps } from 'formik'
import * as Yup from 'yup'
import toast from 'react-hot-toast'
import type { StepData } from '../../../services/onboardingService'
import majorityShareholderService from '../../../services/majorityShareholderService'
import { authService } from '../../../services/authService'
import ContinueLaterModal from '../ContinueLaterModal'

interface LegalRepresentativeData {
  nombre: string
  apellidoPaterno: string
  apellidoMaterno: string
  rfc: string
  numeroCelular: string
  correoElectronico: string
  esMismoAccionistaMayoritario: boolean
}

interface LegalRepresentativeStepProps {
  onComplete?: (stepData: StepData) => Promise<void>
  stepData?: any
  stepKey?: string
}

// Función para extraer fecha de nacimiento del RFC
const extractBirthDateFromRFC = (rfc: string): Date | null => {
  if (!rfc || rfc.length < 10) return null

  try {
    // Los primeros 4 caracteres son las iniciales
    // Los siguientes 6 son la fecha: YYMMDD
    const dateStr = rfc.substring(4, 10)

    if (!/^\d{6}$/.test(dateStr)) return null

    const year = parseInt(dateStr.substring(0, 2))
    const month = parseInt(dateStr.substring(2, 4))
    const day = parseInt(dateStr.substring(4, 6))

    // Validar mes y día
    if (month < 1 || month > 12 || day < 1 || day > 31) return null

    // Determinar el siglo (RFC usa YY, asumimos que años 00-29 son 2000-2029, 30-99 son 1930-1999)
    const fullYear = year <= 29 ? 2000 + year : 1900 + year

    // Crear fecha y validar que sea válida
    const date = new Date(fullYear, month - 1, day)
    if (
      date.getFullYear() !== fullYear ||
      date.getMonth() !== month - 1 ||
      date.getDate() !== day
    ) {
      return null
    }

    return date
  } catch (error) {
    console.error('Error extracting birth date from RFC:', error)
    return null
  }
}

// Función para validar mayoría de edad
const validateMinimumAge = (rfc: string, minimumAge: number = 21): boolean => {
  // Cambiado de 18 a 21
  const birthDate = extractBirthDateFromRFC(rfc)
  if (!birthDate) return true // Si no se puede extraer la fecha, no validamos edad

  const today = new Date()
  let age = today.getFullYear() - birthDate.getFullYear()
  const monthDiff = today.getMonth() - birthDate.getMonth()

  if (
    monthDiff < 0 ||
    (monthDiff === 0 && today.getDate() < birthDate.getDate())
  ) {
    age--
  }

  return age >= minimumAge
}

const validationSchema = Yup.object({
  nombre: Yup.string()
    .trim()
    .min(1, 'El nombre no puede estar vacío')
    .when('esMismoAccionistaMayoritario', {
      is: false,
      then: schema => schema.required('El nombre es requerido'),
      otherwise: schema => schema,
    }),
  apellidoPaterno: Yup.string()
    .trim()
    .min(1, 'El apellido paterno no puede estar vacío')
    .when('esMismoAccionistaMayoritario', {
      is: false,
      then: schema => schema.required('El apellido paterno es requerido'),
      otherwise: schema => schema,
    }),
  apellidoMaterno: Yup.string()
    .trim()
    .min(1, 'El apellido materno no puede estar vacío')
    .when('esMismoAccionistaMayoritario', {
      is: false,
      then: schema => schema.required('El apellido materno es requerido'),
      otherwise: schema => schema,
    }),
  rfc: Yup.string()
    .transform(value => (value ? value.toUpperCase().trim() : value))
    .min(1, 'El RFC no puede estar vacío')
    .matches(
      /^([A-ZÑ\x26]{4}([0-9]{2})(0[1-9]|1[0-2])(0[1-9]|1[0-9]|2[0-9]|3[0-1]))([A-Z\d]{3})$/,
      'El RFC debe tener el formato válido para persona física (4 letras, 6 números, 3 caracteres) y con fecha válida'
    )
    .required('El RFC es requerido')
    .test(
      'minimum-age',
      'La persona debe ser mayor de 21 años', // Cambiado de 18 a 21
      function (value) {
        if (!value) return true // Si no hay valor, no validamos edad
        return validateMinimumAge(value, 21) // Cambiado de 18 a 21
      }
    )
    .when('esMismoAccionistaMayoritario', {
      is: false,
      then: schema => schema.required('El RFC es requerido'),
      otherwise: schema => schema,
    }),
  numeroCelular: Yup.string()
    .trim()
    .min(1, 'El número de celular no puede estar vacío')
    .matches(/^[0-9]{10}$/, 'El número debe tener 10 dígitos')
    .when('esMismoAccionistaMayoritario', {
      is: false,
      then: schema => schema.required('El número de celular es requerido'),
      otherwise: schema => schema,
    }),
  correoElectronico: Yup.string()
    .trim()
    .min(1, 'El correo electrónico no puede estar vacío')
    .email('Correo electrónico inválido')
    .when('esMismoAccionistaMayoritario', {
      is: false,
      then: schema => schema.required('El correo electrónico es requerido'),
      otherwise: schema => schema,
    }),
  esMismoAccionistaMayoritario: Yup.boolean(),
})
const handlePhoneChange = (value: string, setFieldValue: any) => {
  // Remove all non-digit characters and limit to 10 digits
  const digitsOnly = value.replace(/\D/g, '').slice(0, 10)
  setFieldValue('numeroCelular', digitsOnly)
}

// Handle RFC change and validate age
const handleRfcChange = (value: string, setFieldValue: any) => {
  const upperValue = value.toUpperCase()
  setFieldValue('rfc', upperValue)

  // Validate minimum age if RFC is complete
  if (upperValue.length >= 10) {
    if (!validateMinimumAge(upperValue, 18)) {
      toast.error('La persona debe ser mayor de edad (18 años)')
    }
  }
}
const handleNameChange = (
  value: string,
  fieldName: string,
  setFieldValue: any
) => {
  // Only allow letters, spaces, and common name characters (ñ, accents)
  const lettersOnly = value.replace(/[^a-zA-ZáéíóúÁÉÍÓÚñÑ\s]/g, '')
  setFieldValue(fieldName, lettersOnly)
}

const LegalRepresentativeStep: React.FC<LegalRepresentativeStepProps> = ({
  onComplete,
}) => {
  const [formData, setFormData] = useState({
    ineFront: null as File | null,
    ineBack: null as File | null,
  })

  const [previews, setPreviews] = useState({
    ineFront: null as string | null,
    ineBack: null as string | null,
  })

  const [isLoadingShareholderData, setIsLoadingShareholderData] =
    useState(false)
  const formikRef = useRef<FormikProps<LegalRepresentativeData> | null>(null)
  const [shareholderId, setShareholderId] = useState<string | null>(null)

  const handleFileChange =
    (field: 'ineFront' | 'ineBack') => (file: File | null) => {
      setFormData(prev => ({ ...prev, [field]: file }))

      if (file) {
        const reader = new FileReader()
        reader.onloadend = () => {
          setPreviews(prev => ({ ...prev, [field]: reader.result as string }))
        }
        reader.readAsDataURL(file)
      } else {
        setPreviews(prev => ({ ...prev, [field]: null }))
      }
    }

  // Function to convert URL to File object
  const urlToFile = async (
    url: string,
    filename: string,
    mimeType: string
  ): Promise<File> => {
    const response = await fetch(url)
    const blob = await response.blob()
    return new File([blob], filename, { type: mimeType })
  }

  // Function to load majority shareholder data
  const loadMajorityShareholderData = async () => {
    if (!formikRef.current) return

    setIsLoadingShareholderData(true)
    try {
      const creditAppId = await authService.getCurrentCreditApplicationId()
      const data =
        await majorityShareholderService.getForLegalRepresentative(
          creditAppId
        )
      console.log('Loaded majority shareholder data:', data)

      // Store shareholder ID for later use
      console.log('Setting shareholderId to:', data.id)
      setShareholderId(data.id)

      // Fill form fields
      formikRef.current.setFieldValue('nombre', data.firstName)
      formikRef.current.setFieldValue('apellidoPaterno', data.paternalLastName)
      formikRef.current.setFieldValue('apellidoMaterno', data.maternalLastName)
      formikRef.current.setFieldValue('rfc', data.rfc)
      formikRef.current.setFieldValue('numeroCelular', data.phoneNumber)
      formikRef.current.setFieldValue('correoElectronico', data.email)

      // Load INE images if available
      if (data.ineDocuments && data.ineDocuments.length > 0) {
        for (const doc of data.ineDocuments) {
          try {
            const file = await urlToFile(doc.url, doc.fileName, doc.mimeType)

            if (doc.side === 'frente') {
              setFormData(prev => ({ ...prev, ineFront: file }))
              setPreviews(prev => ({ ...prev, ineFront: doc.url }))
            } else if (doc.side === 'reverso') {
              setFormData(prev => ({ ...prev, ineBack: file }))
              setPreviews(prev => ({ ...prev, ineBack: doc.url }))
            }
          } catch (error) {
            console.error(`Error loading INE ${doc.side}:`, error)
          }
        }
      }

      // Force validation after loading data
      setTimeout(() => {
        if (formikRef.current) {
          formikRef.current.validateForm()
        }
      }, 100)

      toast.success('Datos cargados del accionista mayoritario')
    } catch (error: any) {
      console.error('Error loading majority shareholder data:', error)

      // Check if it's a 404 error (no majority shareholder found)
      if (error.response?.status === 404) {
        toast.error('No se encontró información del accionista mayoritario')
      } else {
        toast.error('Error al cargar datos del accionista mayoritario')
      }

      // Uncheck the checkbox if there's an error
      if (formikRef.current) {
        formikRef.current.setFieldValue('esMismoAccionistaMayoritario', false)
      }
    } finally {
      setIsLoadingShareholderData(false)
    }
  }

  // Function to clear form when unchecking
  const clearFormData = () => {
    if (!formikRef.current) return

    setShareholderId(null)
    formikRef.current.setFieldValue('nombre', '')
    formikRef.current.setFieldValue('apellidoPaterno', '')
    formikRef.current.setFieldValue('apellidoMaterno', '')
    formikRef.current.setFieldValue('rfc', '')
    formikRef.current.setFieldValue('numeroCelular', '')
    formikRef.current.setFieldValue('correoElectronico', '')

    setFormData({ ineFront: null, ineBack: null })
    setPreviews({ ineFront: null, ineBack: null })

    // Force validation after clearing data
    setTimeout(() => {
      if (formikRef.current) {
        formikRef.current.validateForm()
      }
    }, 100)
  }

  const initialValues: LegalRepresentativeData = {
    nombre: '',
    apellidoPaterno: '',
    apellidoMaterno: '',
    rfc: '',
    numeroCelular: '',
    correoElectronico: '',
    esMismoAccionistaMayoritario: false,
  }

  const canContinue = formData.ineFront && formData.ineBack

  const handleFormSubmit = async (values: LegalRepresentativeData) => {
    // Trim all string values
    const trimmedValues = {
      ...values,
      nombre: values.nombre.trim(),
      apellidoPaterno: values.apellidoPaterno.trim(),
      apellidoMaterno: values.apellidoMaterno.trim(),
      rfc: values.rfc.trim(),
      numeroCelular: values.numeroCelular.trim(),
      correoElectronico: values.correoElectronico.trim(),
      // Boolean remains as is
      esMismoAccionistaMayoritario: values.esMismoAccionistaMayoritario,
    }

    if (!canContinue) {
      console.log('Cannot continue - missing files')
      return
    }

    try {
      let savedShareholderId = shareholderId

      // If checkbox is marked and we have a shareholder ID, mark as legal representative
      if (trimmedValues.esMismoAccionistaMayoritario && shareholderId) {
        console.log('Calling setAsLegalRepresentative with ID:', shareholderId)
        const result =
          await majorityShareholderService.setAsLegalRepresentative(
            shareholderId
          )
        console.log('setAsLegalRepresentative result:', result)
        toast.success('Representante legal registrado correctamente')
      } else {
        // If checkbox is NOT marked, create a new legal representative
        console.log('Creating new legal representative with form data')
        const creditAppId = await authService.getCurrentCreditApplicationId()
        const result =
          await majorityShareholderService.createLegalRepresentative(
            trimmedValues,
            formData,
            creditAppId
          )
        console.log('createLegalRepresentative result:', result)
        savedShareholderId = result.id
        toast.success('Representante legal creado correctamente')
      }

      // Complete the onboarding step
      if (onComplete) {
        console.log('Calling onComplete...')
        await onComplete({
          completedAt: new Date().toISOString(),
          data: {
            ...trimmedValues,
            ineFiles: formData,
            shareholderId: savedShareholderId,
          },
        })
        console.log('onComplete finished')
      }
    } catch (error: any) {
      console.error('Error saving legal representative:', error)
      toast.error(
        error.response?.data?.message ||
          'Error al guardar el representante legal'
      )
    }
  }

  return (
    <>
      {/* Header */}
      <div className="text-center mb-4">
        <h1 className="text-h3 font-bold text-gray-900 mb-4">
          Datos del Representante Legal
        </h1>
        <p className="text-gray-600 text-paragraph-2">
          Para continuar, necesitamos los datos del Representante Legal. Si es
          la misma persona que el Accionista Mayoritario, puedes marcar la
          casilla para reutilizar esa información y evitar llenarla de nuevo.
        </p>
      </div>

      <Formik
        initialValues={initialValues}
        validationSchema={validationSchema}
        onSubmit={handleFormSubmit}
        innerRef={formikRef}
      >
        {({ values, setFieldValue, errors, touched, handleBlur }) => (
          <Form className="space-y-6">
            {/* Checkbox for same person */}
            <div>
              <label className="flex items-center space-x-3">
                <input
                  type="checkbox"
                  checked={values.esMismoAccionistaMayoritario}
                  onChange={async e => {
                    const isChecked = e.target.checked
                    setFieldValue('esMismoAccionistaMayoritario', isChecked)

                    if (isChecked) {
                      // Load majority shareholder data
                      await loadMajorityShareholderData()
                    } else {
                      // Clear form data
                      clearFormData()
                    }
                  }}
                  disabled={isLoadingShareholderData}
                  className="w-4 h-4 text-blue-600 border-gray-300 rounded focus:ring-blue-500 disabled:opacity-50"
                />
                <span className="text-sm font-medium text-gray-700">
                  {isLoadingShareholderData
                    ? 'Cargando datos...'
                    : 'El Representante legal es el mismo accionista mayoritario'}
                </span>
              </label>
            </div>

            {/* Form Fields */}
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
              <Input
                label="Nombre(s)*"
                name="nombre"
                placeholder="Escribe aquí"
                value={values.nombre}
                onChange={e =>
                  handleNameChange(e.target.value, 'nombre', setFieldValue)
                }
                onBlur={handleBlur}
                error={
                  touched.nombre && errors.nombre ? errors.nombre : undefined
                }
                disabled={values.esMismoAccionistaMayoritario}
              />

              <Input
                label="Apellido paterno*"
                name="apellidoPaterno"
                placeholder="Escribe aquí"
                value={values.apellidoPaterno}
                onChange={e =>
                  handleNameChange(
                    e.target.value,
                    'apellidoPaterno',
                    setFieldValue
                  )
                }
                onBlur={handleBlur}
                error={
                  touched.apellidoPaterno && errors.apellidoPaterno
                    ? errors.apellidoPaterno
                    : undefined
                }
                disabled={values.esMismoAccionistaMayoritario}
              />

              <Input
                label="Apellido materno*"
                name="apellidoMaterno"
                placeholder="Escribe aquí"
                value={values.apellidoMaterno}
                onChange={e =>
                  handleNameChange(
                    e.target.value,
                    'apellidoMaterno',
                    setFieldValue
                  )
                }
                onBlur={handleBlur}
                error={
                  touched.apellidoMaterno && errors.apellidoMaterno
                    ? errors.apellidoMaterno
                    : undefined
                }
                disabled={values.esMismoAccionistaMayoritario}
              />

              <Input
                label="RFC*"
                name="rfc"
                placeholder="Escribe aquí"
                value={values.rfc}
                onChange={e => handleRfcChange(e.target.value, setFieldValue)}
                error={touched.rfc && errors.rfc ? errors.rfc : undefined}
                maxLength={13}
                disabled={values.esMismoAccionistaMayoritario}
                onBlur={handleBlur}
                tooltip="Debe corresponder a una persona mayor de 21 años" // Cambiado de 18 a 21
              />

              <Input
                label="Número de celular*"
                name="numeroCelular"
                placeholder="Escribe aquí"
                value={values.numeroCelular}
                onChange={e => handlePhoneChange(e.target.value, setFieldValue)}
                onBlur={handleBlur}
                error={
                  touched.numeroCelular && errors.numeroCelular
                    ? errors.numeroCelular
                    : undefined
                }
                maxLength={10}
                disabled={values.esMismoAccionistaMayoritario}
              />

              <Input
                label="Correo electrónico*"
                name="correoElectronico"
                type="email"
                placeholder="Escribe aquí"
                value={values.correoElectronico}
                onChange={e =>
                  setFieldValue('correoElectronico', e.target.value)
                }
                onBlur={handleBlur}
                error={
                  touched.correoElectronico && errors.correoElectronico
                    ? errors.correoElectronico
                    : undefined
                }
                disabled={values.esMismoAccionistaMayoritario}
              />
            </div>

            {/* INE Upload Section */}
            <div>
              <h3 className="text-lg font-semibold text-gray-900 mb-1">
                Credencial INE*
              </h3>
              <p className="text-gray-600 mb-4">
                En caso de ser extranjero, por favor sube la foto de tu FM4
              </p>

              <div className="grid grid-cols-1 sm:grid-cols-2 gap-6">
                <FileUpload
                  label="Frente"
                  accept="image/*,.pdf"
                  onFileChange={handleFileChange('ineFront')}
                  preview={previews.ineFront}
                  disabled={values.esMismoAccionistaMayoritario}
                />
                <FileUpload
                  label="Reverso"
                  accept="image/*,.pdf"
                  onFileChange={handleFileChange('ineBack')}
                  preview={previews.ineBack}
                  disabled={values.esMismoAccionistaMayoritario}
                />
              </div>
            </div>

            {/* Campos obligatorios */}
            <div className="pt">
              <p className="text-sm text-gray-600">*Campos obligatorios</p>
            </div>

            {/* Action Buttons */}
            <div className="flex flex-col sm:flex-row justify-center gap-4">
              <Button
                className="px-12 py-3 min-w-[230px] sm:order-2"
                type="submit"
                disabled={!canContinue || isLoadingShareholderData}
              >
                Continuar
              </Button>

              <div className="sm:order-1">
                <ContinueLaterModal />
              </div>
            </div>
          </Form>
        )}
      </Formik>
    </>
  )
}

export default LegalRepresentativeStep
